import {
  Icon,
  SelectedTabProvider,
  Tab,
  Tabs,
} from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import React, { ReactNode, useEffect, useState } from 'react'
import { FulfilmentType } from '../../../../models/fulfilment'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectCalculateTaxError,
  selectIsEcomOrder,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { useSaleHasServiceOrder } from '../../../sale/useSaleHasServiceOrder'
import { LineItemTab } from '../../../../../types/lineItemTabs'
import { useHasOutdatedTaxCategory } from '../fulfilment/hook/useHasOutdatedTaxCategory'
import { useLineItemHasFulfillmentAddressError } from '../fulfilment/hook/useLineItemHasFulfillmentAddressError'
import { selectSaleReturnFor } from '../../../../state/selectors'
import { Tooltip } from '../../../shared/Tooltip'

export interface LineItemTabsProps {
  defaultTab?: LineItemTab
  onChange: (selected: LineItemTab) => void
  children?: ReactNode | undefined
  fulfillmentType?: FulfilmentType
  lineItemId?: string
}

export const LineItemTabsLegacy: React.FC<LineItemTabsProps> = ({
  defaultTab = LineItemTab.DETAIL,
  onChange,
  children,
  fulfillmentType,
  lineItemId,
}) => {
  const t = useT()
  const isReferencedReturn = !!useAppSelector(selectSaleReturnFor)
  const initialTab =
    isReferencedReturn && defaultTab === LineItemTab.FULFILMENT
      ? LineItemTab.DETAIL
      : defaultTab
  const [currentTab, setCurrentTab] = useState(initialTab)
  // Keep the tooltip open on keyboard focus because the uncontrolled focus
  // handler in Unified Components does not transition the tooltip state.
  const [
    isFulfillmentTooltipFocused,
    setIsFulfillmentTooltipFocused,
  ] = useState(false)
  const isEcomOrder = useAppSelector(selectIsEcomOrder)
  const saleHasServiceOrder = useSaleHasServiceOrder()
  const calculateTaxError = useAppSelector(selectCalculateTaxError)
  const hasAddressErrorForLineItemFulfillmentDelivery =
    (useLineItemHasFulfillmentAddressError() || !!calculateTaxError) &&
    fulfillmentType === FulfilmentType.DELIVERY
  const hasOutdatedTaxCategory =
    useHasOutdatedTaxCategory(lineItemId) &&
    fulfillmentType === FulfilmentType.DELIVERY

  const hasFulfillmentError =
    hasAddressErrorForLineItemFulfillmentDelivery || hasOutdatedTaxCategory

  const [displayErrorIcon, setDisplayErrorIcon] = useState(hasFulfillmentError)

  useEffect(() => {
    setDisplayErrorIcon(
      currentTab !== LineItemTab.FULFILMENT &&
        hasFulfillmentError &&
        !isEcomOrder
    )
  }, [currentTab, hasFulfillmentError, isEcomOrder])

  useEffect(() => {
    const nextTab =
      isReferencedReturn && defaultTab === LineItemTab.FULFILMENT
        ? LineItemTab.DETAIL
        : defaultTab
    setCurrentTab(nextTab)
    if (nextTab !== defaultTab) {
      onChange(nextTab)
    }
  }, [defaultTab, isReferencedReturn, onChange])

  const shouldShowTabs = !saleHasServiceOrder

  // Reset to Details tab when tabs are hidden (e.g., when service order is added while viewing Fulfillment tab)
  useEffect(() => {
    if (!shouldShowTabs && currentTab !== LineItemTab.DETAIL) {
      setCurrentTab(LineItemTab.DETAIL)
      onChange(LineItemTab.DETAIL)
    }
  }, [shouldShowTabs, currentTab, onChange])

  return (
    <SelectedTabProvider
      defaultTab={initialTab}
      onChange={string => {
        const selectedTab = string as LineItemTab
        setCurrentTab(selectedTab)
        onChange(selectedTab)
      }}
    >
      {/* When tabs are hidden, thisBox provides the vd-mb3 margin that Tabs would normally provide */}
      <div className={shouldShowTabs ? undefined : 'vd-mb3'}>
        {shouldShowTabs && (
          <Tabs className="vd-mb3">
            <Tab data-track="details" name={LineItemTab.DETAIL}>
              {t('Details', { _context: 'Line item tabs' })}
            </Tab>
            {isReferencedReturn ? (
              <div
                onFocusCapture={() => setIsFulfillmentTooltipFocused(true)}
                onBlurCapture={() => setIsFulfillmentTooltipFocused(false)}
              >
                <Tooltip
                  opened={isFulfillmentTooltipFocused || undefined}
                  placement="bottom"
                  controlSlot={
                    <div>
                      <Tab
                        data-track="fulfillment"
                        name={LineItemTab.FULFILMENT}
                        disabled
                      >
                        {t('Fulfillment', { _context: 'Line item tabs' })}
                      </Tab>
                    </div>
                  }
                >
                  {t('Fulfillment method can’t be added for returns.', {
                    _context: 'Fulfillment tab disabled tooltip message',
                  })}
                </Tooltip>
              </div>
            ) : (
              <Tab data-track="fulfillment" name={LineItemTab.FULFILMENT}>
                {displayErrorIcon && (
                  <span data-testid="line-item-icon-error" className="vd-mr2">
                    <Icon
                      icon="exclamation-triangle"
                      className="wr-input-error-icon"
                    />
                  </span>
                )}
                {t('Fulfillment', { _context: 'Line item tabs' })}
              </Tab>
            )}
          </Tabs>
        )}
      </div>
      {children}
    </SelectedTabProvider>
  )
}
